(() => {
  const SELECTOR = '[data-collection-featured-gifts]';
  const ALLOWED_TEMPLATES = new Set([
    'collection-page-1',
    'collection-page-2',
    'collection-page-3',
  ]);

  const keepBrowsingOnCollection = () => {
    const experience = document.querySelector(
      'collection-ajax-experience[data-cardology-collection-template]'
    );
    return ALLOWED_TEMPLATES.has(
      experience?.dataset.cardologyCollectionTemplate || ''
    );
  };

  class CollectionFeaturedGifts {
    constructor(root) {
      this.root = root;
      this.slider = root.querySelector('[data-gift-slider]');
      this.controls = root.querySelector('[data-gift-controls]');
      this.prev = root.querySelector('[data-gift-prev]');
      this.next = root.querySelector('[data-gift-next]');
      this.forms = Array.from(root.querySelectorAll('[data-gift-form]'));
      this.resizeObserver = null;
      this.onResize = this.debounce(() => this.updateControls(), 120);
      this.onCollectionUpdated = () => this.syncWithProductGrid();

      if (!this.slider) return;

      this.bind();
      this.syncWithProductGrid();
      this.updateControls();
      requestAnimationFrame(() => this.updateControls());
    }

    bind() {
      this.prev?.addEventListener('click', () => this.scroll('left'));
      this.next?.addEventListener('click', () => this.scroll('right'));
      this.slider.addEventListener('scroll', () => this.updateArrowStates(), {
        passive: true,
      });
      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('collection:updated', this.onCollectionUpdated);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this.slider);
      }

      if (!keepBrowsingOnCollection()) {
        this.forms.forEach((form) => {
          form.addEventListener('submit', (event) => this.addToCart(event, form));
        });
      }
    }

    syncWithProductGrid() {
      const visibleProductIds = new Set(
        Array.from(
          document.querySelectorAll('[data-product-grid-item][data-product-id]')
        )
          .map((item) => item.dataset.productId)
          .filter(Boolean)
      );

      let firstVisibleCard = null;
      let visibleCount = 0;

      this.slider.querySelectorAll('.cfg__card[data-product-id]').forEach((card) => {
        const duplicate = visibleProductIds.has(card.dataset.productId);
        card.hidden = duplicate;

        const badge = card.querySelector('[data-gift-badge]');
        if (badge) badge.hidden = true;

        if (!duplicate) {
          visibleCount += 1;
          if (!firstVisibleCard) firstVisibleCard = card;
        }
      });

      const firstBadge = firstVisibleCard?.querySelector('[data-gift-badge]');
      if (firstBadge) firstBadge.hidden = false;

      this.root.hidden = visibleCount === 0;
      this.updateControls();
    }

    scroll(direction) {
      const card = Array.from(
        this.slider.querySelectorAll('.cfg__card:not([hidden])')
      )[0];
      const gap = parseFloat(
        getComputedStyle(this.slider).columnGap ||
          getComputedStyle(this.slider).gap ||
          '16'
      );
      const distance = card ? card.getBoundingClientRect().width + gap : 320;

      this.slider.scrollBy({
        left: direction === 'right' ? distance : -distance,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
      });
    }

    hasOverflow() {
      if (!this.slider || this.root.hidden) return false;
      return this.slider.scrollWidth - this.slider.clientWidth > 2;
    }

    updateControls() {
      if (!this.controls) return;

      const showControls =
        window.matchMedia('(min-width: 768px)').matches && this.hasOverflow();
      this.controls.hidden = !showControls;
      this.controls.setAttribute('aria-hidden', showControls ? 'false' : 'true');

      if (showControls) this.updateArrowStates();
    }

    updateArrowStates() {
      if (!this.hasOverflow()) return;

      const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
      const current = Math.max(0, this.slider.scrollLeft);

      if (this.prev) this.prev.disabled = current <= 2;
      if (this.next) this.next.disabled = current >= maxScroll - 2;
    }

    async addToCart(event, form) {
      event.preventDefault();

      const button = form.querySelector('[data-gift-add]');
      const card = form.closest('.cfg__card');
      const error = form.querySelector('[data-gift-error]');
      const label = button?.querySelector('[data-gift-label]');
      const variantId = form.querySelector('[name="id"]')?.value;

      if (!button || !variantId) return;

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }

      try {
        const cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');

        if (cart?.getSectionsToRender) {
          formData.append(
            'sections',
            cart
              .getSectionsToRender()
              .map((section) => section.id)
              .join(',')
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement?.(document.activeElement);
        }

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });

        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(
            data.description || data.message || 'Unable to add this item.'
          );
        }

        card?.setAttribute('data-added', 'true');
        if (label) label.textContent = button.dataset.addedLabel || 'Added';

        if (cart?.renderContents && data.sections) {
          cart.renderContents(data, 1);
        } else {
          document.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true })
          );
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'collection-featured-gifts',
            productVariantId: variantId,
          });
        }

        document.dispatchEvent(
          new CustomEvent('product:added', {
            bubbles: true,
            detail: { variantId, source: 'collection-featured-gifts' },
          })
        );
      } catch (requestError) {
        if (error) {
          error.textContent = requestError.message || 'Unable to add this item.';
          error.hidden = false;
        }
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }

    debounce(callback, delay) {
      let timer;
      return (...args) => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => callback(...args), delay);
      };
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.dataset.giftsInitialized === 'true') return;
      root.dataset.giftsInitialized = 'true';
      new CollectionFeaturedGifts(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();